import { useMemo, useState } from "react";
import { Form, Formik } from "formik";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { NewProfileApplicationFrame } from "./NewProfileApplicationFrame";
import { Box } from "../../../components/Box";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { H2 } from "../../../components/Heading";
import { Input } from "../../../components/Input";
import { Paragraph } from "../../../components/Paragraph";
import Select from "../../../components/Select";
import { ROUTE } from "../../../routes";
import { addMandateValidation } from "../../../utils/validationSchema";
import { useConsultants } from "../../../data/useConsultants";
import { useMandateTypes } from "../../../data/useMandateTypes";
import { useMembershipDivisionTeam } from "../../../hooks/useMembershipDivisionTeam";
import { useCreateMandate } from "../../../data/useCreateMandate";
import { useCreateConsultant } from "../../../data/useCreateConsultant";
import { useCreateIndividual } from "../../../data/useCreateIndividual";
import { logError, showError } from "../../../utils/error-handling";
import styled from "styled-components";

const MandateTypeBubble = ({
  title,
  content,
  children,
}: {
  title: string;
  content?: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <Box borderRadius={0} bg="#e3e7ee" p={3} mb={2}>
    <Paragraph mt="0px" fontWeight="bold">
      {title}
    </Paragraph>
    {content ? <Paragraph mb="0px">{content}</Paragraph> : children}
  </Box>
);

const StyledUl = styled.ul`
  ${({ theme: { fontSizes, fontFamily } }) => `     
    font-family: ${fontFamily};
    font-size: ${fontSizes[2]};
    padding-left: 12px;
    margin-bottom: 0px;
  `}
`;

const StyledLi = styled.li`
  ${({ theme: { space, colors } }) => `
    margin-left: ${space[2]};
    ::marker { color: ${colors.gray[40]}; }
  `}
`;

export const ApplicationTypeStep = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const profileId = Number(id);

  const { profile } = useOutletContext<{ profile: any }>();

  const [doubleClick, setDoubleClick] = useState(false);
  const [selectedMandateType, setSelectedMandateType] = useState<number | null>(
    null,
  );

  const {
    divisionOptions,
    showDivisionField,
    singleDivisionId,
    teamOptionsFor,
    singleTeamId,
  } = useMembershipDivisionTeam();

  const { data: consultants } = useConsultants();
  const { data: mandateTypes, isLoading: isMandateTypesLoading } = useMandateTypes(
    profile?._type === "entity" || profile?._type === "individual"
      ? profile._type
      : undefined,
  );

  const { mutate: createMandate } = useCreateMandate();
  const { mutate: createConsultant } = useCreateConsultant();
  const { mutate: createIndividual } = useCreateIndividual();

  const consultantOptionsRaw = useMemo(() => {
    const results = consultants?.data?.results ?? [];
    return results
      .filter((c: any) => c.active)
      .map((c: any) => ({ label: c.name, value: c.id }));
  }, [consultants]);

  const consultantOptions = useMemo(
    () => [
      { value: "create_consultant", label: "Add a new consultant" },
      ...consultantOptionsRaw,
    ],
    [consultantOptionsRaw],
  );

  const applicationTypeOptions = useMemo(() => {
    const list = mandateTypes?.data ?? [];
    return list.map((m: any) => ({ label: m.name, value: m.id }));
  }, [mandateTypes]);

  const mandateTypeDetails = useMemo(() => {
    const list = mandateTypes?.data ?? [];
    return list.find((m: any) => m.id === selectedMandateType);
  }, [mandateTypes, selectedMandateType]);

  const buildIndividualPayload = (mandateId: number) => {
    if (!profile) return null;
    if (profile._type !== "individual") return null;

    const firstName = profile.first_name ?? null;
    const lastName = profile.last_name ?? null;
    const email = profile.email ?? null;
    const date_of_birth = profile.date_of_birth ?? null;
    const gender = profile.gender ?? null;

    // if (!firstName || !lastName || !email) {
    //   return null;
    // }

    return {
      title: profile.title?.trim() || null,
      first_name: firstName,
      middle_name: profile.middle_name ?? null,
      last_name: lastName,
      date_of_birth: date_of_birth,
      gender: gender,
      email,
      external_reference: profile.external_reference ?? null,
      mandate: mandateId,
      profile: profileId,
    };
  };

  const buildEntityPayload = (mandateId: number) => {
    if (!profile) return null;
    if (profile._type !== "entity") return null;

    const companyName = profile?.name ?? "";
    const companyStatus =
      typeof profile?.company_status === "number"
        ? profile.company_status
        : typeof profile?.company_status?.id === "number"
        ? profile.company_status.id
        : null;
    const country = profile?.country?.id ?? null;
    const entityType = profile?.entity_type?.id ?? null;
    const registrationNumber = profile?.registration_number ?? "";
    const dateOfIncorporation = profile?.date_of_incorporation ?? null;

    // if (
    //   !mandateId ||
    //   !companyName
    //   // !country
    //   // !entityType ||
    //   // !registrationNumber ||
    //   // !dateOfIncorporation
    // ) {
    //   return null;
    // }

    return {
      mandate: mandateId,
      company_name: companyName,
      company_status: companyStatus,
      country: country,
      entity_type: Number(entityType),
      registration_number: registrationNumber,
      date_of_incorporation: dateOfIncorporation,
      external_reference: profile.external_reference ?? null,

      profile: profileId,
    };
  };

  const onSubmit = (values: any) => {
    if (doubleClick) return;
    setDoubleClick(true);

    const reference = (values.applicationReference ?? "").trim();

    const navigateNext = (
      mandateId: number,
      createdRecordId?: number,
      entityPayload?: any,
    ) => {
      const nextRoute =
        profile?._type === "individual"
          ? ROUTE.PROFILE_APPLICATION_SEND_INVITES
          : ROUTE.PROFILE_APPLICATION_ADD_CONTACT;

      navigate(nextRoute.replace(":id", profileId.toString()), {
        state: {
          mandateId,
          createdRecordId,
          applicationReference: reference,
          applicationType: values.applicationType,
          consultant: values.consultant,
          consultantName: values.consultantName,
          consultantEmail: values.consultantEmail,
          entityPayload,
        },
      });
    };

    const createProfileRecordAfterMandate = (mandateId: number) => {
      if (profile?._type === "entity") {
        const payload = buildEntityPayload(mandateId);
        if (!payload) {
          navigateNext(mandateId);
          return;
        }

        navigateNext(mandateId, undefined, payload);
        return;
      }

      if (profile?._type === "individual") {
        const payload = buildIndividualPayload(mandateId);

        if (!payload) {
          navigateNext(mandateId);
          return;
        }

        createIndividual(payload as any, {
          onSuccess: ({ data }: any) => {
            navigateNext(mandateId, data?.id);
          },
          onError: (err: any) => {
            setDoubleClick(false);

            showError(
              err,
              "Mandate created, but failed to create the individual.",
            );
          },
        });

        return;
      }

      navigateNext(mandateId);
    };

    const createMandateWithConsultant = (consultantId: number) => {
      createMandate(
        {
          name: reference,
          reference,
          mandate_type: values.applicationType,
          consultant: consultantId,
          category: "Application",
          primary_division: values.division,
          primary_team: values.team,
          _type: profile?._type,
        },
        {
          onSuccess: ({ data }: any) => {
            createProfileRecordAfterMandate(data.id);
          },
          onError: (err: any) => {
            setDoubleClick(false);
            showError(
              err,
              "A problem occurred while creating the application.",
            );
          },
        },
      );
    };

    if (values.consultant === "create_consultant") {
      createConsultant(
        {
          name: values.consultantName,
          email: values.consultantEmail,
        },
        {
          onSuccess: ({ data }: any) => {
            createMandateWithConsultant(data.id);
          },
          onError: (err: any) => {
            setDoubleClick(false);

            if (err?.response?.data?.email?.[0]) {
              showError(err, err.response.data.email[0]);
            } else {
              showError(
                err,
                "A problem occurred while creating the consultant.",
              );
            }
            logError(err);
          },
        },
      );
      return;
    }

    createMandateWithConsultant(values.consultant);
  };

  return (
    <NewProfileApplicationFrame step="applicationType">
      <>
        <Box mt={2}>
          <Formik
            initialValues={{
              applicationReference: "",
              division: singleDivisionId,
              team: singleTeamId,
              applicationType: "",
              consultant: "",
              consultantName: "",
              consultantEmail: "",
            }}
            enableReinitialize
            validationSchema={addMandateValidation}
            onSubmit={onSubmit}
          >
            {(props) => {
              const canNext = props.isValid && props.dirty && !doubleClick;

              return (
                <Form style={{ width: 510 }} onSubmit={props.handleSubmit}>
                  <H2>Application Type</H2>
                  <Paragraph>
                    Please select the type of application you would like to
                    create.
                  </Paragraph>

                  <Input
                    onChange={props.handleChange}
                    value={props.values.applicationReference}
                    placeholder="Enter application reference"
                    mt="3"
                    label="Application reference"
                    name="applicationReference"
                    isRequired
                    hasError={Boolean(props.errors.applicationReference)}
                    errorMessage={props.errors.applicationReference as any}
                  />

                  <Select
                    onChange={(option: any) => {
                      props.setFieldValue("applicationType", option.value);
                      setSelectedMandateType(option.value);
                    }}
                    value={applicationTypeOptions?.find(
                      (o: any) => o.value === props.values.applicationType,
                    )}
                    options={applicationTypeOptions}
                    isLoading={isMandateTypesLoading}
                    placeholder="Select application type"
                    mt="3"
                    label="Application type"
                    name="applicationType"
                    isRequired
                    hasError={Boolean(props.errors.applicationType)}
                    errorMessage={props.errors.applicationType as any}
                  />

                  {showDivisionField && (
                    <Select
                      onChange={(option: any) => {
                        const newDivisionTeams = teamOptionsFor(option.value);
                        props.setValues({
                          ...props.values,
                          division: option.value,
                          team:
                            newDivisionTeams.length === 1
                              ? newDivisionTeams[0].value
                              : "",
                        });
                      }}
                      value={divisionOptions?.find(
                        ({ value }: any) => value === props.values.division,
                      )}
                      options={divisionOptions}
                      placeholder="Choose division"
                      mt="3"
                      label="Division"
                      name="division"
                      isRequired
                      hasError={Boolean(props.errors.division)}
                      errorMessage={props.errors.division as any}
                    />
                  )}

                  {teamOptionsFor(props.values.division).length > 0 && (
                    <Select
                      key={props.values.division}
                      onChange={(option: any) =>
                        props.setFieldValue("team", option.value)
                      }
                      value={
                        teamOptionsFor(props.values.division).find(
                          ({ value }: any) => value === props.values.team,
                        ) ?? null
                      }
                      options={teamOptionsFor(props.values.division)}
                      placeholder="Choose team"
                      mt="3"
                      label="Team"
                      name="team"
                      isRequired
                      isDisabled={!props.values.division}
                      hasError={Boolean(props.errors.team)}
                      errorMessage={props.errors.team as any}
                    />
                  )}

                  <Select
                    onChange={(option: any) =>
                      props.setFieldValue("consultant", option.value)
                    }
                    value={consultantOptions?.find(
                      (o: any) => o.value === props.values.consultant,
                    )}
                    options={consultantOptions}
                    placeholder="Select consultant"
                    mt="3"
                    label="Select consultant"
                    name="consultant"
                  />

                  {props.values.consultant === "create_consultant" && (
                    <>
                      <Input
                        onChange={props.handleChange}
                        value={props.values.consultantName}
                        placeholder="Enter consultant name"
                        mt="3"
                        label="Consultant Name"
                        name="consultantName"
                        isRequired
                        hasError={Boolean(props.errors.consultantName)}
                        errorMessage={props.errors.consultantName as any}
                      />
                      <Input
                        onChange={props.handleChange}
                        value={props.values.consultantEmail}
                        placeholder="Enter consultant email"
                        mt="3"
                        label="Consultant Email"
                        name="consultantEmail"
                        isRequired
                        hasError={Boolean(props.errors.consultantEmail)}
                        errorMessage={props.errors.consultantEmail as any}
                      />
                    </>
                  )}

                  <Flex justifyContent="space-between" mt={3}>
                    <Button
                      variant="text"
                      type="button"
                      onClick={() =>
                        navigate(
                          ROUTE.PROFILE_APPLICATION_ENTITY_DETAILS.replace(
                            ":id",
                            profileId.toString(),
                          ),
                        )
                      }
                    >
                      Back
                    </Button>

                    <Button type="submit" isDisabled={!canNext}>
                      Next
                    </Button>
                  </Flex>

                  <Box display="none">{selectedMandateType}</Box>
                </Form>
              );
            }}
          </Formik>
        </Box>

        {selectedMandateType && mandateTypeDetails && (
          <Box
            display={{ _: "none", xl: "block" }}
            width="325px"
            flexShrink={0}
            position="absolute"
            top="16px"
            right="32px"
          >
            <Paragraph fontWeight="bold">
              {mandateTypeDetails?.name} includes:
            </Paragraph>

            <MandateTypeBubble
              title="Description"
              content={mandateTypeDetails?.description}
            />

            <MandateTypeBubble title="Screening">
              <StyledUl>
                {(mandateTypeDetails?.checkpoint ?? []).map(
                  ({ actual_name }: any, index: number) => (
                    <StyledLi key={index}>{actual_name}</StyledLi>
                  ),
                )}
              </StyledUl>
            </MandateTypeBubble>

            <MandateTypeBubble title="Forms">
              {mandateTypeDetails?._form &&
              mandateTypeDetails._form.length > 0 ? (
                <StyledUl>
                  {mandateTypeDetails._form.map((f: any, index: number) => (
                    <StyledLi key={index}>{f?._form?.name}</StyledLi>
                  ))}
                </StyledUl>
              ) : (
                <StyledUl>
                  <StyledLi>None</StyledLi>
                </StyledUl>
              )}
            </MandateTypeBubble>

            <MandateTypeBubble title="Documents">
              {mandateTypeDetails?.additional_document &&
              mandateTypeDetails.additional_document.length > 0 ? (
                <StyledUl>
                  {mandateTypeDetails.additional_document.map(
                    (doc: any, index: number) => (
                      <StyledLi key={index}>{doc?.name}</StyledLi>
                    ),
                  )}
                </StyledUl>
              ) : (
                <StyledUl>
                  <StyledLi>None</StyledLi>
                </StyledUl>
              )}
            </MandateTypeBubble>
          </Box>
        )}
      </>
    </NewProfileApplicationFrame>
  );
};
